import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { LanguageContext } from '../../contexts/LanguageContext';
import { ThemeContext } from '../../contexts/ThemeContext';

const PhysicalSecurity = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{translations['physical.pageTitle'] || 'Physical Security | Risk Consulting | Fepsec'}</title>
        <meta name="description" content="Professional physical security consulting services to protect your assets, properties, and personnel" />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-aos="fade-up">{translations['physical.heroTitle'] || 'Physical Security Consulting'}</h1>
            <p className="text-xl text-blue-100 mb-8" data-aos="fade-up" data-aos-delay="100">
              {translations['physical.heroSubtitle'] || 'Comprehensive security solutions to protect your most valuable assets and personnel'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">{translations['physical.mainTitle'] || 'Protecting What Matters Most'}</h2>
            <p className="text-gray-700 dark:text-white text-lg mb-6">
              {translations['physical.mainDescription1'] || 'At Fepsec, our Physical Security services focus on creating comprehensive protection strategies for your organization\'s physical assets, facilities, and personnel. We understand that effective physical security is the first line of defense against a wide range of threats, from unauthorized access to theft and vandalism.'}
            </p>
            <p className="text-gray-700 dark:text-white text-lg mb-6">
              {translations['physical.mainDescription2'] || 'Our team of security experts brings decades of combined experience from various sectors including government, military, commercial, and private security industries. We leverage this diverse expertise to deliver tailored physical security solutions that address your specific risk profile and organizational requirements.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-aos="fade-up" data-aos-delay="100">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">{translations['physical.approachTitle'] || 'Our Approach'}</h3>
              <ul className="space-y-3 text-gray-700 dark:text-white">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.approach1'] || 'Comprehensive risk assessment and vulnerability analysis'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.approach2'] || 'Development of tailored security master plans'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.approach3'] || 'Security design for new facilities or renovations'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.approach4'] || 'Implementation of layered security measures'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.approach5'] || 'Security personnel training and development'}</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-aos="fade-up" data-aos-delay="200">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">{translations['physical.benefitsTitle'] || 'Key Benefits'}</h3>
              <ul className="space-y-3 text-gray-700 dark:text-white">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.benefit1'] || 'Reduced security incidents and associated costs'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.benefit2'] || 'Enhanced protection of critical assets and personnel'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.benefit3'] || 'Optimized security investments with maximum ROI'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.benefit4'] || 'Compliance with industry standards and regulations'}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>{translations['physical.benefit5'] || 'Increased stakeholder confidence and peace of mind'}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-10" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">{translations['physical.servicesTitle'] || 'Our Physical Security Services'}</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-3">{translations['physical.service1Title'] || 'Security Assessment & Planning'}</h3>
                <p className="text-gray-700 dark:text-white">
                  {translations['physical.service1Desc'] || 'We conduct thorough security assessments to identify vulnerabilities in your current security posture and develop comprehensive security master plans that address existing and potential threats while aligning with your organizational objectives.'}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-3">{translations['physical.service2Title'] || 'Facility Security Design'}</h3>
                <p className="text-gray-700 dark:text-white">
                  {translations['physical.service2Desc'] || 'Our security design services integrate Crime Prevention Through Environmental Design (CPTED) principles and best practices to create secure environments that deter criminal activity while maintaining aesthetic appeal and functionality.'}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-3">{translations['physical.service3Title'] || 'Access Control Systems'}</h3>
                <p className="text-gray-700 dark:text-white">
                  {translations['physical.service3Desc'] || 'We design and implement sophisticated access control solutions that regulate entry to your facilities, ensuring that only authorized personnel can access sensitive areas while maintaining detailed access logs for security auditing.'}
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-3">{translations['physical.service4Title'] || 'Security Procedures & Training'}</h3>
                <p className="text-gray-700 dark:text-white">
                  {translations['physical.service4Desc'] || 'We develop customized security procedures and conduct comprehensive training programs for your security personnel and employees to ensure everyone understands their role in maintaining a secure environment.'}
                </p>
              </div>
            </div>
          </div>

          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">{translations['physical.ctaTitle'] || 'Ready to Enhance Your Physical Security?'}</h2>
            <p className="text-gray-700 dark:text-white text-lg mb-8">
              {translations['physical.ctaDescription'] || 'Contact our team today to schedule a consultation and discover how our physical security solutions can protect your organization\'s most valuable assets.'}
            </p>
            <a 
              href="/contact-us" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300 transform hover:scale-105"
            >
              {translations['physical.ctaButton'] || 'Contact Us'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalSecurity;
